import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import noLogo from "../../../assets/img/brand/noLogo";
import moment from "moment";
import { toTitleCase } from "@dhn/shared/utils";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const NO_INSURED_ARS_ID = 27;

const isNonInsuredArs = (item = {}) => {
  const normalizedName = `${item?.arsName || item?.insurerName || ""}`
    .toUpperCase()
    .trim();

  return (
    Number(item?.arsId) === NO_INSURED_ARS_ID ||
    normalizedName.includes("NO ASEGURADO") ||
    normalizedName.includes("NO ASEGURADA")
  );
};

const dailyCloseReport = (data = {}) => {
  const {
    date,
    centerName,
    doctorName,
    userName,
    insuranceSales,
    coPayments,
    handleValueModeOfSale,
    handleValueDiferencia,
  } = data;

  const safeText = (v, fallback = "—") => {
    const s = (v ?? "").toString().trim();
    return s.length ? s : fallback;
  };

  const toNum = (v, def = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : def;
  };

  const formatDate = (value) => {
    const parsed = moment(value || new Date());
    if (parsed.isValid()) {
      return parsed.format("DD-MM-YYYY");
    }

    return moment().format("DD-MM-YYYY");
  };

  const money = (v) => {
    const n = toNum(v, 0);
    return Math.max(0, Math.round((n + Number.EPSILON) * 100) / 100);
  };

  const fmtMoney = (() => {
    const nf = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    });
    return (v) => nf.format(money(v));
  })();

  // ---------------------------
  // Totales (si no vienen, se calculan)
  // ---------------------------
  const sum = (arr, pick) => arr.reduce((acc, it) => acc + money(pick(it)), 0);

  const filteredInsuranceSales = (insuranceSales || []).filter(
    (item) => !isNonInsuredArs(item),
  );

  const insuredSubtotal =
    data?.insuredSubtotal ?? sum(filteredInsuranceSales, (r) => r?.coverage);
  const copaySubtotal =
    data?.copaySubtotal ?? sum(coPayments, (r) => r?.diferencia ?? r?.copago);

  // ---------------------------
  // Estilos de layout
  // ---------------------------

  const layoutNoBorder = {
    hLineWidth: () => 0,
    vLineWidth: () => 0,
    paddingLeft: () => 0,
    paddingRight: () => 0,
    paddingTop: () => 0,
    paddingBottom: () => 0,
  };

  // ---------------------------
  // Builders (para que se lea bonito)
  // ---------------------------
  const sectionTitle = (t) => ({
    text: t,
    style: "sectionTitle",
    margin: [0, 10, 0, 6],
  });

  const hr = (y = 0.9) => ({
    canvas: [{ type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: y }],
    margin: [0, 6, 0, 8],
  });

  const makeLogoBlock = () => {
    if (data?.logoDataUrl) return { image: data.logoDataUrl, width: 120 };

    return {
      layout: layoutNoBorder,
      width: 140,
      table: {
        widths: ["*"],
        body: [[{ image: noLogo, width: 150, margin: [0, 0, 0, 0] }]],
      },
    };
  };

  const makeHeaderRight = () => ({
    stack: [
      {},
      {
        text: "Reporte de Cierre Diario",
        style: "title",
        alignment: "right",
        fontSize: 14,
        margin: [0, 50, 0, 0],
      },
      {
        text: `Fecha: ${formatDate(date)}`,
        alignment: "right",
        margin: [0, 6, 0, 0],
        bold: true,
        fontSize: 10,
      },
    ],
  });

  const makeInfoBlock = () => ({
    table: {
      widths: ["50%", "50%"],
      body: [
        [
          {
            text: [
              {
                text: "Centro: ",
                style: "infoLine",
                fontSize: 12,
              },
              {
                text: `${safeText(centerName)}`,
                fontSize: 12,
                decoration: "underline",
              },
            ],
          },
          {
            text: [
              {
                text: "Médico: ",
                style: "infoLine",
                fontSize: 12,
              },
              {
                text: `${toTitleCase(safeText(doctorName))}`,
                fontSize: 12,
                decoration: "underline",
              },
            ],
            style: "infoLine",
            alignment: "right",
            margin: [0, 0, 0, 0],
          },
        ],
      ],
    },
    layout: layoutNoBorder,
    margin: [0, 10, 0, 6],
  });

  const makeInsuredTable = () => {
    const header = [
      { text: "Aseguradora", style: "th", alignment: "center" },
      { text: "No. Servicios", style: "th", alignment: "center" },
      { text: "Valor\nContratado", style: "th", alignment: "center" },
      { text: "Cobertura", style: "th", alignment: "center" },
    ];

    const rows = filteredInsuranceSales.map((r) => [
      { text: safeText(r?.arsName), style: "td" },
      {
        text: String(toNum(r?.quantityOfServices, 0)),
        style: "td",
        alignment: "center",
      },
      { text: fmtMoney(r?.contrated), style: "td", alignment: "right" },
      { text: fmtMoney(r?.coverage), style: "td", alignment: "right" },
    ]);

    const subtotalRow = [
      {
        text: "Sub-Total",
        colSpan: 3,
        alignment: "center",
        bold: true,
        fontSize: 10,
      },
      {},
      {},
      {
        text: fmtMoney(insuredSubtotal),
        alignment: "right",
        bold: true,
        fontSize: 10,
      },
    ];

    return {
      table: {
        headerRows: 1,
        widths: ["*", 70, 90, 80],
        body: [header, ...rows, subtotalRow],
      },
    };
  };

  const makeCopayTable = () => {
    const header = [
      { text: "Modo de Pago", style: "th", alignment: "center" },
      { text: "No. Servicios", style: "th", alignment: "center" },
      { text: "Valor\nFacturado", style: "th", alignment: "center" },
      { text: "Valor\nCo-Pagos", style: "th", alignment: "center" },
    ];

    const rows = coPayments.map((r) => [
      { text: safeText(r?.paymentMethodName), style: "td" },
      {
        text: String(toNum(r?.quantityOfServices, 0)),
        style: "td",
        alignment: "center",
      },
      { text: fmtMoney(r?.fee), style: "td", alignment: "right" },
      { text: fmtMoney(r?.diferencia ?? r?.copago), style: "td", alignment: "right" },
    ]);

    const subtotalRow = [
      {
        text: "Sub-Total",
        colSpan: 3,
        alignment: "center",
        bold: true,
        fontSize: 10,
      },
      {},
      {},
      {
        text: fmtMoney(copaySubtotal),
        alignment: "right",
        bold: true,
        fontSize: 10,
      },
    ];

    return {
      table: {
        headerRows: 1,
        widths: ["*", 70, 90, 80],
        body: [header, ...rows, subtotalRow],
      },
    };
  };

  const makeDaySalesTable = () => ({
    table: {
      headerRows: 1,
      widths: [110, 110],
      body: [
        [
          { text: "Modo de Venta", style: "th", alignment: "center" },
          { text: "Valor Facturado", style: "th", alignment: "center" },
        ],
        [
          { text: "Seguro Médico", style: "td" },
          {
            text: fmtMoney(handleValueModeOfSale()),
            style: "td",
            alignment: "right",
          },
        ],
        [
          { text: "Co-Pagos", style: "td" },
          {
            text: fmtMoney(handleValueDiferencia()),
            style: "td",
            alignment: "right",
          },
        ],
        [
          { text: "Total", style: "td", bold: true, alignment: "center" },
          {
            text: fmtMoney(handleValueModeOfSale() + handleValueDiferencia()),
            style: "td",
            bold: true,
            alignment: "right",
          },
        ],
      ],
    },
    width: 260,
    margin: [0, 0, 0, 8],
  });

  const makeSignature = () => ({
    stack: [
      {
        canvas: [
          { type: "line", x1: 160, y1: 0, x2: 360, y2: 0, lineWidth: 0.9 },
        ],
        margin: [0, 22, 0, 6],
      },
      {
        text: safeText(userName, "Nombre de Usuario"),
        alignment: "center",
        fontSize: 10,
      },
      {
        text: "Firma",
        alignment: "center",
        fontSize: 10,
        margin: [0, 4, 0, 0],
      },
    ],
    margin: [0, 10, 0, 0],
  });

  // ---------------------------
  // docDefinition
  // ---------------------------
  return {
    pageMargins: [30, 30, 30, 20],
    styles: {
      title: { fontSize: 14, bold: true, decoration: "underline" },
      infoLine: { fontSize: 9, bold: true },
      sectionTitle: { fontSize: 9, bold: true, decoration: "underline" },
      th: { bold: true, fontSize: 9 },
      td: { fontSize: 9 },
    },
    content: [
      // Header
      {
        columns: [
          { width: 160, ...makeLogoBlock() },
          { width: "*", ...makeHeaderRight() },
        ],
        columnGap: 10,
      },

      // Info
      makeInfoBlock(),

      // Separator
      hr(),

      // Tables
      sectionTitle("Ventas por Seguro"),
      makeInsuredTable(),

      sectionTitle("Co-Pagos"),
      makeCopayTable(),

      sectionTitle("Venta del Día"),
      makeDaySalesTable(),

      // Signature
      makeSignature(),
    ],
  };
};

export const openDailyCloseReport = (data = {}) => {
  pdfMake.createPdf(dailyCloseReport(data)).open();
};
